// modules/voice/index.ts — Platik-IA
// Entrada por voz (Web Speech API) + interpretación via Gemini (Edge Function proxy).

import { bus }       from '../../core/event-bus';
import { supabase }  from '../../services/supabase';
import { env }       from '../../core/env';
import type { VoiceAPI }         from './contracts';
import type { ParsedTransaction } from '../../shared/types/global';

interface SRAlternative  { transcript: string; confidence: number; }
interface SRResult       { readonly length: number; [i: number]: SRAlternative; }
interface SRResultList   { readonly length: number; [i: number]: SRResult; }
interface SREvent        extends Event { results: SRResultList; }
interface SRErrorEvent   extends Event { error: string; }
interface SRInstance {
  lang: string; continuous: boolean; interimResults: boolean; maxAlternatives: number;
  onresult: ((e: SREvent)      => void) | null;
  onerror:  ((e: SRErrorEvent) => void) | null;
  onend:    (() => void) | null;
  start(): void; stop(): void;
}
type SRConstructor = new () => SRInstance;

declare global {
  interface Window {
    SpeechRecognition?:       SRConstructor;
    webkitSpeechRecognition?: SRConstructor;
  }
}

let _recognition: SRInstance | null = null;
let _isListening  = false;

function getSpeechRecognition(): SRConstructor | null {
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

/** ¿Está habilitada la respuesta en audio? (toggle de settings; default ON). */
function ttsEnabled(): boolean {
  const saved = localStorage.getItem('platik:audioEnabled');
  return saved === null ? true : saved === 'true';
}

/** Frase corta de confirmación a partir de lo interpretado por la IA. */
function confirmationPhrase(p: ParsedTransaction): string {
  const tipo  = p.tipo === 'Ingreso' ? 'Ingreso' : 'Gasto';
  const parts = [tipo];
  if (p.monto != null) parts.push(`de ${p.monto} pesos`);
  const detalle = p.descripcion || p.subcategoria || p.categoria;
  if (detalle) parts.push(`en ${detalle}`);
  return parts.join(' ') + '. Revisá y guardá.';
}

/** Pronuncia un texto con la voz elegida en settings, si TTS está habilitado. */
function speakText(text: string): void {
  if (!('speechSynthesis' in window) || !text || !ttsEnabled()) return;
  const synth = window.speechSynthesis;
  synth.cancel();   // evita superposición con una locución previa
  const u = new SpeechSynthesisUtterance(text);
  const wanted    = localStorage.getItem('platik:voiceName');
  const voices    = synth.getVoices();
  const devLang   = navigator.language || 'es-AR';   // idioma del dispositivo
  const devPrefix = devLang.split('-')[0];
  const voice =
    (wanted && voices.find(v => v.name === wanted)) ||
    voices.find(v => v.lang.replace('_', '-') === devLang) ||
    voices.find(v => v.lang.replace('_', '-').startsWith(devPrefix)) ||
    voices.find(v => v.lang.startsWith('es'));
  if (voice) { u.voice = voice; u.lang = voice.lang; } else { u.lang = devLang; }
  synth.speak(u);
}

export async function initVoice(): Promise<VoiceAPI> {
  const SR = getSpeechRecognition();

  if (SR) {
    _recognition = new SR();
    _recognition.lang           = 'es-AR';
    _recognition.continuous     = false;
    _recognition.interimResults = false;
    _recognition.maxAlternatives = 1;

    _recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      bus.emit('voice:result', { text });
      // Parsear automáticamente
      api.parseText(text).then(parsed => {
        bus.emit('voice:parsed', { parsed });
      }).catch(err => {
        bus.emit('voice:error', { message: err.message });
      });
    };

    _recognition.onerror = (event) => {
      _isListening = false;
      bus.emit('voice:stopped');
      if (event.error !== 'no-speech') {
        bus.emit('voice:error', { message: `Error de micrófono: ${event.error}` });
      }
    };

    _recognition.onend = () => {
      _isListening = false;
      bus.emit('voice:stopped');
    };
  }

  const api: VoiceAPI = {
    isSupported: () => SR !== null,

    startListening() {
      if (!_recognition || _isListening) return;
      _isListening = true;
      _recognition.start();
      bus.emit('voice:started');
    },

    stopListening() {
      if (!_recognition || !_isListening) return;
      _recognition.stop();
      _isListening = false;
      bus.emit('voice:stopped');
    },

    async parseText(text: string): Promise<ParsedTransaction> {
      // Obtener categorías actuales del módulo categories
      const catsModule = (await import('../../core/registry')).getModule<{ getAll: () => Record<string, string[]> }>('categories');
      const categories = catsModule?.getAll() ?? {};

      // Obtener token de sesión para la Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No hay sesión activa');

      // Fecha de hoy en hora local (yyyy-mm-dd) para que la IA resuelva fechas relativas.
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const today = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

      const res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/gemini-proxy`, {
        method:  'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify({ text, categories, today }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(err.error ?? `Error ${res.status}`);
      }

      return res.json() as Promise<ParsedTransaction>;
    },

    speak(text: string) {
      speakText(text);
    },

    dispose() {
      if (_recognition && _isListening) _recognition.stop();
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
      bus.off('voice:parsed', onParsed);
      _recognition = null;
    },
  };

  // Respuesta en audio: al interpretar una operación, confirmar por voz lo entendido.
  const onParsed = ({ parsed }: { parsed: ParsedTransaction }) => {
    speakText(confirmationPhrase(parsed));
  };
  bus.on('voice:parsed', onParsed);

  bus.emit('voice:ready');
  return api;
}
